/**
 * Logger bridge - provides access to the legacy logger service
 * The logger service (web/services/logger.js) exposes window.log via loglevel
 */

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';

export interface Logger {
  trace: (message: string, trace?: string) => void;
  debug: (message: string, trace?: string) => void;
  info: (message: string, trace?: string) => void;
  warn: (message: string, trace?: string) => void;
  error: (message: string, trace?: string) => void;
}

declare global {
  interface Window {
    log?: Logger;
  }
}

// Fallback logger for when window.log is not available (e.g., dev mode)
const fallbackLogger: Logger = {
  trace: (msg) => console.debug('[TRACE]', msg),
  debug: (msg) => console.debug('[DEBUG]', msg),
  info: (msg) => console.info('[INFO]', msg),
  warn: (msg) => console.warn('[WARN]', msg),
  error: (msg) => console.error('[ERROR]', msg),
};

/**
 * Get the logger instance
 * Returns window.log if available (in main app), otherwise a console fallback
 */
export function getLogger(): Logger {
  return window.log ?? fallbackLogger;
}

/**
 * Create a prefixed logger for a specific component/module
 * @param prefix - Prefix to add to all log messages (e.g., component name)
 */
export function createLogger(prefix: string): Logger {
  const logger = getLogger();
  const formatMessage = (msg: string) => `[${prefix}] ${msg}`;

  return {
    trace: (msg, trace) => logger.trace(formatMessage(msg), trace),
    debug: (msg, trace) => logger.debug(formatMessage(msg), trace),
    info: (msg, trace) => logger.info(formatMessage(msg), trace),
    warn: (msg, trace) => logger.warn(formatMessage(msg), trace),
    error: (msg, trace) => logger.error(formatMessage(msg), trace),
  };
}

/**
 * Check if the legacy logger is available
 */
export function isLoggerAvailable(): boolean {
  return typeof window.log !== 'undefined';
}
